// https://evanw.github.io/font-texture-generator/

import { generateTextMesh, generateVerticalTextMesh } from "./generateTextMesh";

export interface FontCharacter {
  x: number;
  y: number;
  width: number;
  height: number;
  originX: number;
  originY: number;
  advance: number;
}

export interface FontDescription {
  name: string;
  size: number;
  bold?: boolean;
  italic?: boolean;
  width: number;
  height: number;
  characters: Partial<Record<string, FontCharacter>>;
}

export type TextAlignment = "left" | "center" | "right";

export interface HorizontalTextParams {
  align?: TextAlignment;
}

export interface VerticalTextParams {
  verticalSpacing: number;
  spaceHeight: number;
}

export interface HorizontalTextSpec extends HorizontalTextParams {
  type: "horizontal";
  text: string;
}

export interface VerticalTextSpec extends VerticalTextParams {
  type: "vertical";
  text: string;
}

export type TextSpec = HorizontalTextSpec | VerticalTextSpec;

export function generateTextMeshBuffer({
  gl,
  font,
  spec,
}: {
  gl: WebGL2RenderingContext;
  font: FontDescription;
  spec: TextSpec;
}): {
  vertexBuffer: WebGLBuffer;
  indexBuffer: WebGLBuffer;
  vertexCount: number;
} {
  const { vertices, letterIndices } =
    spec.type === "vertical"
      ? generateVerticalTextMesh(font, spec)
      : generateTextMesh(font, spec);

  const vertexBuffer = gl.createBuffer();
  const indexBuffer = gl.createBuffer();

  if (!vertexBuffer || !indexBuffer) {
    throw new Error("Failed to create buffer");
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);

  gl.bindBuffer(gl.ARRAY_BUFFER, indexBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Uint16Array(letterIndices),
    gl.STATIC_DRAW,
  );

  gl.bindBuffer(gl.ARRAY_BUFFER, null);

  return {
    vertexBuffer: vertexBuffer,
    indexBuffer: indexBuffer,
    vertexCount: vertices.length / 4,
  };
}
